import { useState, useEffect } from "react";
import { Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";

// Simple password gate for private investment dealrooms.
// Password verified client-side, then unlocked state persists in sessionStorage.
// Magic links: if URL has `?t=<token>`, we call the backend to verify and auto-unlock.

const PASSWORD = "Applesaft@123";
const STORAGE_KEY = "ire_dealroom_unlocked";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const DealroomLock = ({ children, projectName = "Private Dealroom" }) => {
  const [unlocked, setUnlocked] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(STORAGE_KEY) === "ok";
  });
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState(false);
  const [magicState, setMagicState] = useState("idle"); // idle | checking | invited | expired
  const [invitedClient, setInvitedClient] = useState("");

  // Magic-link check on mount — token verification is intentional side-effect on first render
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const t = params.get("t");
    if (!t || unlocked) return;
    let cancelled = false;

    const run = async () => {
      setMagicState("checking");
      try {
        const r = await fetch(`${BACKEND_URL}/api/dealroom/share/verify?t=${encodeURIComponent(t)}`);
        const data = await r.json();
        if (cancelled) return;
        if (data.valid) {
          sessionStorage.setItem(STORAGE_KEY, "ok");
          setInvitedClient(data.client || "");
          setMagicState("invited");
          setTimeout(() => { if (!cancelled) setUnlocked(true); }, 900);
        } else {
          setMagicState(data.reason === "expired" ? "expired" : "idle");
        }
      } catch {
        if (!cancelled) setMagicState("idle");
      }
    };
    run();
    return () => { cancelled = true; };
  }, [unlocked]);

  const submit = (e) => {
    e.preventDefault();
    if (pw === PASSWORD) {
      sessionStorage.setItem(STORAGE_KEY, "ok");
      // The password IS the team password — anyone who types it is the team.
      // Persist to localStorage so the Share-with-client FAB activates automatically
      // on every future visit on this device. Clients use magic links and never type it.
      try { localStorage.setItem("ire_admin_pw", PASSWORD); } catch { /* private mode */ }
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
      setTimeout(() => setError(false), 1800);
    }
  };

  if (unlocked) return children;

  return (
    <div className="fixed inset-0 z-[100] bg-ire-black flex items-center justify-center px-6" data-testid="dealroom-lock">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-ire-accent/[0.18] rounded-full blur-[180px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-ire-accent/[0.12] rounded-full blur-[160px]" />
      </div>

      <div className="relative w-full max-w-md bg-[#080808] border border-white/[0.08] p-10 md:p-12">
        {/* Magic link states */}
        {magicState === "checking" && (
          <div data-testid="dealroom-magic-checking">
            <div className="flex items-center gap-3 mb-2">
              <Lock className="w-4 h-4 text-ire-accent animate-pulse" />
              <span className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase">Verifying invitation</span>
            </div>
            <h2 className="font-heading text-2xl text-white mb-3 leading-tight">Checking your access link…</h2>
            <div className="flex gap-1.5 mt-6">
              <span className="w-2 h-2 bg-ire-accent rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-2 h-2 bg-ire-accent/60 rounded-full animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="w-2 h-2 bg-ire-accent/40 rounded-full animate-bounce" style={{ animationDelay: "240ms" }} />
            </div>
          </div>
        )}

        {magicState === "invited" && (
          <div data-testid="dealroom-magic-invited">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="font-body text-[10px] tracking-[0.3em] text-emerald-400 uppercase">Welcome</span>
            </div>
            <h2 className="font-heading text-3xl text-white mb-3 leading-tight">
              {invitedClient ? `Welcome, ${invitedClient}.` : "Welcome."}
            </h2>
            <p className="font-body text-sm text-white/55 leading-relaxed">Opening the {projectName} investment case…</p>
          </div>
        )}

        {magicState === "expired" && (
          <div className="mb-6 p-3 border border-amber-400/30 bg-amber-400/[0.06] text-amber-200 font-body text-xs leading-relaxed" data-testid="dealroom-magic-expired">
            Your invitation link has expired. Please contact your advisor for a new link, or enter the access code below.
          </div>
        )}

        {(magicState === "idle" || magicState === "expired") && (
          <form onSubmit={submit}>
            <div className="flex items-center gap-3 mb-2">
              <Lock className="w-4 h-4 text-ire-accent" />
              <span className="font-body text-[10px] tracking-[0.3em] text-ire-accent uppercase">Private dealroom</span>
            </div>
            <h2 className="font-heading text-3xl text-white mb-3 leading-tight">{projectName}</h2>
            <p className="font-body text-sm text-white/55 mb-8 leading-relaxed">
              This investment case is shared with qualified IRE clients only.
              Enter the access code provided by your advisor to continue.
            </p>

            <label className="block">
              <span className="font-body text-[10px] tracking-[0.25em] uppercase text-white/55 mb-2 block">Access code</span>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  autoFocus
                  placeholder="Enter code"
                  data-testid="dealroom-password-input"
                  className={`w-full bg-black/60 border ${error ? "border-rose-400/60" : "border-white/[0.12] focus:border-ire-accent"} text-white font-mono text-base py-3.5 pl-4 pr-12 transition-all outline-none placeholder:text-white/25`}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  data-testid="dealroom-password-toggle"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-ire-accent"
                  tabIndex={-1}
                >
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </label>
            {error && (
              <div className="mt-3 font-body text-xs text-rose-300" data-testid="dealroom-password-error">
                Incorrect code. Please try again or contact your advisor.
              </div>
            )}

            <button
              type="submit"
              data-testid="dealroom-password-submit"
              className="w-full mt-7 py-4 bg-ire-accent text-black font-body text-xs tracking-[0.25em] uppercase hover:bg-white transition-all"
            >
              Unlock investment case
            </button>

            <p className="mt-8 pt-6 border-t border-white/[0.06] font-body text-[11px] text-white/35 leading-relaxed">
              Not an IRE client yet? <a href="/apply" className="text-ire-accent hover:text-white underline underline-offset-2">Request qualification</a> to receive the dealroom code.
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default DealroomLock;
